import React, { forwardRef, ReactNode } from 'react'
import { styled } from '@mui/material/styles'
import { NavLink, NavLinkProps } from 'react-router-dom'
import { rgba, darken } from 'polished'

import { Chip, Collapse, ListItemProps, ListItemButton, ListItemText } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { SidebarTitle } from '../../enums/SidebarTitle'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomRouterLink = forwardRef<any, NavLinkProps>((props, ref) => (
  <div ref={ref}>
    <NavLink {...props} />
  </div>
))

type ItemType = {
  activeclassname?: string
  onClick?: () => void
  to?: string
  component?: typeof NavLink
  depth: number
}

const Item = styled(ListItemButton)<ItemType>`
  padding-top: ${(props) => props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-bottom: ${(props) => props.theme.spacing(props.depth && props.depth > 0 ? 2 : 3)};
  padding-left: ${(props) => props.theme.spacing(props.depth && props.depth > 0 ? 14 : 8)};
  padding-right: ${(props) => props.theme.spacing(props.depth && props.depth > 0 ? 4 : 7)};
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  svg {
    color: ${(props) => props.theme.sidebar.color};
    font-size: 20px;
    width: 20px;
    height: 20px;
    opacity: 0.5;
  }
  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: ${(props) => props.theme.sidebar.color};
  }
  &.${(props) => props.activeclassname} {
    background-color: ${(props) => darken(0.03, props.theme.sidebar.background)};
    span {
      color: ${(props) => props.theme.sidebar.color};
    }
  }
`

type TitleType = {
  depth: number
}

const Title = styled(ListItemText)<TitleType>`
  margin: 0;
  span {
    color: ${(props) => rgba(props.theme.sidebar.color, props.depth && props.depth > 0 ? 0.7 : 1)};
    font-size: ${(props) => props.theme.typography.body1.fontSize}px;
    padding: 0 ${(props) => props.theme.spacing(4)};
  }
`

const Badge = styled(Chip)`
  font-weight: ${(props) => props.theme.typography.fontWeightBold};
  height: 20px;
  position: absolute;
  right: 26px;
  top: 12px;
  background: ${(props) => props.theme.sidebar.badge.background};
  z-index: 1;
  span.MuiChip-label,
  span.MuiChip-label:hover {
    font-size: 11px;
    cursor: pointer;
    color: ${(props) => props.theme.sidebar.badge.color};
    padding-left: ${(props) => props.theme.spacing(2)};
    padding-right: ${(props) => props.theme.spacing(2)};
  }
`

const ExpandLessIcon = styled(ExpandLess)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`

const ExpandMoreIcon = styled(ExpandMore)`
  color: ${(props) => rgba(props.theme.sidebar.color, 0.5)};
`

type SidebarNavListItemProps = ListItemProps & {
  className?: string
  depth: number
  href: string
  icon: ReactNode
  badge?: string
  open?: boolean
  title: string
}

const SidebarNavListItem: React.FC<SidebarNavListItemProps> = (props) => {
  const { title, href, depth = 0, children, icon, badge, open: openProp = false } = props

  const [open, setOpen] = React.useState(openProp)

  const handleToggle = () => {
    setOpen((state) => !state)
  }

  const [t] = useTranslation('common')
  const isActionItem = href.startsWith('action:')

  const handleActionClick = () => {
    if (!isActionItem) {
      return
    }

    if (href === 'action:open-ai-chat') {
      window.dispatchEvent(new CustomEvent('spine:open-ai-agent-chat'))
    }
  }

  const renderIcon = () => {
    if (!icon) {
      return null
    }

    if (React.isValidElement(icon)) {
      return icon
    }

    const IconComponent = icon as React.ElementType

    return <IconComponent />
  }
  const translateTitle = (title: SidebarTitle) => {
    switch (title) {
      case SidebarTitle.FRONTPAGE:
        return t('Sidebar.frontpage')
      case SidebarTitle.AI_AGENT:
        return t('Sidebar.aiAgent', { defaultValue: 'AI-agentti' })
      case SidebarTitle.PROJECTS:
        return t('Sidebar.projects')
      case SidebarTitle.POWERSUPPLIES:
        return t('Sidebar.powerSupplies')
      case SidebarTitle.DEVICES:
        return t('Sidebar.devices')
      case SidebarTitle.USERS:
        return t('Sidebar.users')
      case SidebarTitle.COMPANIES:
        return t('Sidebar.companies')
      case SidebarTitle.CONDITION_MONITORING:
        return t('Sidebar.condition')
      case SidebarTitle.MANUFACTURING:
        return t('Sidebar.manufacturing')
      case SidebarTitle.INSTALL_NEW_DEVICE:
        return t('Sidebar.installNewDevice')
      case SidebarTitle.REPLACE_DEVICE:
        return t('Sidebar.replaceDevice')
      case SidebarTitle.ADD_NEW_RUUVI_TAG:
        return t('Sidebar.addNewRuuviTag')
      case SidebarTitle.ANNOUNCEMENT_MANAGEMENT:
        return t('announcements')
      case SidebarTitle.HEATERS:
        return t('heaters')
      case SidebarTitle.GENERATORS:
        return t('generators')
      case SidebarTitle.ANALYTICS:
        return t('analytics')
      case SidebarTitle.AURORAS:
        return 'Arctic Warm'
    }
  }
  if (children) {
    return (
      <React.Fragment>
        <Item depth={depth} onClick={handleToggle}>
          {renderIcon()}
          <Title depth={depth}>
            {title}
            {badge && <Badge label={badge} />}
          </Title>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Item>
        <Collapse in={open}>{children}</Collapse>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Item
        depth={depth}
        component={isActionItem ? undefined : CustomRouterLink}
        to={isActionItem ? undefined : href}
        activeclassname="active"
        onClick={isActionItem ? handleActionClick : undefined}
      >
        {renderIcon()}
        <Title depth={depth}>
          {translateTitle(title as SidebarTitle)}
          {badge && <Badge label={badge} />}
        </Title>
      </Item>
    </React.Fragment>
  )
}

export default SidebarNavListItem
